import { errorFormating } from "utils/utils";

// Orden secuencial de los pasos de cada empleado.
export const STEP_ORDER = [
	"create",
	"contract",
	"group",
	"function",
	"shift_pattern",
];

export const FIELD_STEP = {
	name: "create",
	surname: "create",
	email: "create",
	role: "create",
	start_day: "create",
	contract_id: "contract",
	group_id: "group",
	function_id: "function",
	shift_pattern_id: "shift_pattern",
};

export const STEP_FIELD = {
	contract: "contract_id",
	group: "group_id",
	function: "function_id",
	shift_pattern: "shift_pattern_id",
};

const CREATE_ERROR_FIELDS = new Set(["name", "surname", "email"]);

export const isOk = (res) => !!res && res.ok !== false;

export const extractError = (res) => {
	const formatted = errorFormating(res || {});
	return formatted.general || "";
};

/** Mapea errores del backend a campos concretos del formulario. */
export const mapApiErrorsToFields = (res, stepKey) => {
	const formatted = errorFormating(res || {});
	const errors = {};

	for (const [key, msg] of Object.entries(formatted)) {
		if (key === "general" || key.endsWith("_value")) continue;

		if (stepKey === "create" && CREATE_ERROR_FIELDS.has(key)) {
			errors[key] = msg;
		} else if (STEP_FIELD[stepKey] === key) {
			errors[key] = msg;
		}
	}

	return errors;
};

export const buildSteps = (row, api) => {
	const startDay = row.start_day;
	const steps = [
		{
			key: "create",
			needed: true,
			run: () =>
				api.add({
					name: row.name.trim(),
					surname: row.surname.trim(),
					email: row.email.trim(),
					start_day: startDay,
					roles: [row.role || "EMPLOYEE"],
					enabled: true,
					with_access: true,
				}),
		},
		{
			key: "contract",
			needed: Number(row.contract_id) > 0,
			run: (empId) =>
				api.addContract(empId, {
					contract_id: row.contract_id,
					start_day: startDay,
					end_day: null,
				}),
		},
		{
			key: "group",
			needed: Number(row.group_id) > 0,
			run: (empId) =>
				api.addGroup(empId, {
					group_id: row.group_id,
					start_day: startDay,
					end_day: null,
				}),
		},
		{
			key: "function",
			needed: Number(row.function_id) > 0,
			run: (empId) =>
				api.addFunction(empId, {
					function_id: row.function_id,
					is_default: true,
					priority: null,
					ubications: [],
				}),
		},
		{
			key: "shift_pattern",
			needed: Number(row.shift_pattern_id) > 0,
			run: (empId) =>
				api.addShiftPattern(empId, {
					shift_pattern_id: row.shift_pattern_id,
					start_day: startDay,
					end_day: null,
					start_index: 0,
				}),
		},
	];

	return steps.sort(
		(a, b) => STEP_ORDER.indexOf(a.key) - STEP_ORDER.indexOf(b.key)
	);
};

export const processRow = async (row, api, t = (k, d) => d) => {
	const result = {
		...row,
		steps: { ...row.steps },
		errors: {},
		rowError: "",
	};
	let empId = result._empId;
	let failed = false;

	for (const step of buildSteps(row, api)) {
		if (failed) {
			// Solo marcamos notsent en steps posteriores si el empleado ya existe.
			if (empId != null && step.needed && result.steps[step.key] !== "done") {
				result.steps[step.key] = "notsent";
			}
			continue;
		}
		if (result.steps[step.key] === "done") continue;
		if (!step.needed) {
			result.steps[step.key] = "skipped";
			continue;
		}

		try {
			const res = await step.run(empId);

			if (step.key === "create") {
				if (!res || !res.id || res.ok === false) {
					failed = true;
					result.steps.create = "failed";
					const fieldErrors = mapApiErrorsToFields(res, "create");
					result.errors = { ...result.errors, ...fieldErrors };
					result.rowError =
						Object.keys(fieldErrors).length === 0
							? extractError(res) ||
							  t(
									"bulkAddEmployees.createFailed",
									"No se pudo crear el empleado."
							  )
							: "";
					continue;
				}
				empId = res.id;
				result._empId = empId;
				result.steps.create = "done";
			} else if (!isOk(res)) {
				failed = true;
				result.steps[step.key] = "failed";
				const fieldErrors = mapApiErrorsToFields(res, step.key);
				result.errors = { ...result.errors, ...fieldErrors };
				if (!result.rowError) {
					result.rowError =
						fieldErrors[STEP_FIELD[step.key]] || extractError(res);
				}
			} else {
				result.steps[step.key] = "done";
			}
		} catch (err) {
			failed = true;
			result.steps[step.key] = "failed";
			if (step.key === "create" && !result.rowError) {
				result.rowError = t(
					"bulkAddEmployees.createFailed",
					"No se pudo crear el empleado."
				);
			}
		}
	}

	result._failed = failed;
	return result;
};

export const processBulkRows = (rows, api, t) =>
	Promise.all(rows.map((row) => processRow(row, api, t)));

/** Bloquea un campo cuando su step ya se completó. */
export const isFieldDisabled = (row, field, loading = false) => {
	if (loading) return true;
	const step = FIELD_STEP[field];
	if (!step) return false;
	return (row.steps || {})[step] === "done";
};

/** Solo marca en rojo campos con error concreto; la fecha nunca por fallo de step. */
export const hasCellError = (row, field) => {
	if (row.errors && row.errors[field]) return true;

	// La fecha solo se marca por validación local, no por fallos de API/step.
	if (field === "start_day") return false;

	const step = FIELD_STEP[field];
	if (!step || step === "create") return false;

	const status = (row.steps || {})[step];
	return status === "failed" || status === "notsent";
};

export const getFieldErrorMessage = (row, field, t = (k, d) => d) => {
	if (row.errors && row.errors[field]) return row.errors[field];

	const step = FIELD_STEP[field];
	if (!step || step === "create") return "";

	const status = (row.steps || {})[step];
	if (status === "failed")
		return t("bulkAddEmployees.stepFailed", "Falló el envío");
	if (status === "notsent")
		return t("bulkAddEmployees.stepNotSent", "No se envió");
	return "";
};

/** Indica si conviene mostrar el mensaje genérico a nivel de fila. */
export const shouldShowRowError = (row) => {
	if (!row.rowError) return false;
	// Si el error ya está en un campo concreto, no duplicar bajo la fila.
	const mappedToField = Object.keys(row.errors || {}).some(
		(f) => row.errors[f] === row.rowError
	);
	return !mappedToField;
};
